/**
 * APP 内 IM 客服对接（Android / iOS）
 *
 * 显示规则：
 * 1. URL im_sw（APP 拼接）：1 直接展示入口，0 隐藏；会话内缓存，内部跳转仍生效
 * 2. 无 im_sw 时调用 CheckImSupport(deviceId, entry) 查询是否展示
 *    - Android：同步返回 1 / 0
 *    - iOS：postMessage 传参，iOS 通过 callbackIMSupport 异步回调 1 / 0
 * 3. getDeviceInfoByIccid 仅由 packagePurchase 页面请求，结果缓存后供 CheckImSupport 使用
 * 4. 当前页面 URL 无 iccid / deviceId 时不展示（首页 topup、paySuccess、关于我们等；localStorage 不算）
 * 5. paySuccess 等指定页面强制不展示
 * 6. 点击客服入口调用 OpenIM，entry_addr 固定 lock_card_4g
 * 7. 点击反馈入口调用 OpenFeedback，参数同 OpenIM
 * 8. 无原生 Bridge 则隐藏（非 APP 环境）
 *
 * 开发 / 测试环境 mock：
 * - mock_im=1 / 0：直接显示 / 隐藏
 * - mock_im=ios / android：注入 Bridge 走完整流程
 * - mock_im_result=1 / 0：控制 Bridge 返回值（默认 1）
 */
export const IM_ENTRY = 'lock_card_4g';

const IM_SW_SESSION_KEY = 'customer_service_im_sw';
const IM_SUPPORT_SESSION_KEY = 'customer_service_im_support';
const DEVICE_ID_BY_ICCID_KEY = 'customer_service_device_id_by_iccid';
const HIDDEN_ROUTES = ['/paySuccess', '/topup'];
const PACKAGE_PURCHASE_ROUTE = '/packagePurchase';
const DEVICE_ID_UPDATED_EVENT = 'customer-service-device-id-updated';
const IS_DEV = process.env.NODE_ENV === 'development';
const ENABLE_CS_DEBUG = IS_DEV || process.env.VUE_APP_ENABLE_CS_DEBUG === 'true';
const ENABLE_CS_MOCK = IS_DEV || process.env.VUE_APP_ENABLE_CS_MOCK === 'true';

function getUrlParams() {
	const params = new URLSearchParams(window.location.search);
	const hash = window.location.hash || '';
	const qIndex = hash.indexOf('?');
	if (qIndex !== -1) {
		new URLSearchParams(hash.slice(qIndex + 1)).forEach((value, key) => {
			if (!params.has(key)) {
				params.set(key, value);
			}
		});
	}
	return params;
}

function getQueryValue(route, key) {
	const query = (route && route.query) || {};
	const value = query[key];
	if (value !== undefined && value !== null && value !== '') {
		return String(value);
	}
	const fromUrl = getUrlParams().get(key);
	return fromUrl === null || fromUrl === '' ? null : fromUrl;
}

function getDevMockImMode(route) {
	if (!ENABLE_CS_MOCK) {
		return null;
	}
	return getQueryValue(route, 'mock_im');
}

function getDevMockImResult(route) {
	const value = getQueryValue(route, 'mock_im_result');
	if (value === null) {
		return 1;
	}
	return Number(value) === 1 ? 1 : 0;
}

function setupMockIosBridge(result) {
	if (!window.webkit) {
		window.webkit = {};
	}
	if (!window.webkit.messageHandlers) {
		window.webkit.messageHandlers = {};
	}
	window.webkit.messageHandlers.CheckImSupport = {
		postMessage(params) {
			console.log('[CustomerService][mock] iOS CheckImSupport:', params);
			setTimeout(() => {
				if (typeof window.callbackIMSupport === 'function') {
					window.callbackIMSupport(result);
				}
			}, 300);
		},
	};
	window.webkit.messageHandlers.OpenIM = {
		postMessage(payload) {
			console.log('[CustomerService][mock] iOS OpenIM:', payload);
		},
	};
	window.webkit.messageHandlers.OpenFeedback = {
		postMessage(payload) {
			console.log('[CustomerService][mock] iOS OpenFeedback:', payload);
		},
	};
}

function setupMockAndroidBridge(result) {
	window.android = window.android || {};
	window.android.CheckImSupport = (deviceId, entry) => {
		console.log('[CustomerService][mock] Android CheckImSupport:', deviceId, entry);
		return result;
	};
	window.android.OpenIM = (payload) => {
		console.log('[CustomerService][mock] Android OpenIM:', payload);
	};
	window.android.OpenFeedback = (payload) => {
		console.log('[CustomerService][mock] Android OpenFeedback:', payload);
	};
}

function applyDevMockBridge(route) {
	const mode = getDevMockImMode(route);
	if (!mode) {
		return;
	}

	const result = getDevMockImResult(route);
	if (mode === 'ios') {
		setupMockIosBridge(result);
		return;
	}
	if (mode === 'android') {
		setupMockAndroidBridge(result);
	}
}

function isDevMockBridgeMode(route) {
	const mode = getDevMockImMode(route);
	return mode === 'ios' || mode === 'android';
}

function logDevTrace(message, payload) {
	if (!ENABLE_CS_DEBUG) {
		return;
	}
	if (payload !== undefined) {
		console.log(`[CustomerService] ${message}`, payload);
		return;
	}
	console.log(`[CustomerService] ${message}`);
}

function resolveDevMockImSupport(route) {
	const mode = getDevMockImMode(route);
	if (!mode) {
		return null;
	}
	if (mode === '1' || mode === 'true') {
		console.log('[CustomerService][mock] force show');
		return true;
	}
	if (mode === '0' || mode === 'false') {
		console.log('[CustomerService][mock] force hide');
		return false;
	}
	return null;
}

/** 仅当前 URL 中的 iccid（不含 localStorage） */
function getIccidFromUrl(route) {
	return getQueryValue(route, 'iccid') || '';
}

function getDeviceIdFromUrl(route) {
	return getQueryValue(route, 'deviceId') || '';
}

function hasDeviceInCurrentUrl(route) {
	return !!(getQueryValue(route, 'iccid') || getQueryValue(route, 'deviceId'));
}

/** 当前页面是否允许展示客服入口 */
function canShowOnCurrentRoute(route) {
	if (route && HIDDEN_ROUTES.includes(route.path)) {
		return false;
	}
	if (route && route.path === PACKAGE_PURCHASE_ROUTE) {
		return !!(
			getIccidFromUrl(route) ||
			localStorage.getItem('iccid') ||
			getDeviceIdFromUrl(route)
		);
	}
	return hasDeviceInCurrentUrl(route);
}

function getCachedDeviceIdByIccid(iccid) {
	try {
		const raw = sessionStorage.getItem(DEVICE_ID_BY_ICCID_KEY);
		if (!raw) {
			return null;
		}
		const map = JSON.parse(raw);
		return map[iccid] || null;
	} catch (e) {
		return null;
	}
}

function setCachedDeviceIdByIccid(iccid, deviceId) {
	try {
		const raw = sessionStorage.getItem(DEVICE_ID_BY_ICCID_KEY);
		const map = raw ? JSON.parse(raw) : {};
		map[iccid] = deviceId;
		sessionStorage.setItem(DEVICE_ID_BY_ICCID_KEY, JSON.stringify(map));
	} catch (e) {
		/* ignore */
	}
}

/** 读取当前 URL im_sw：1=支持、0=不支持；无参数返回 null */
function getImSwFromUrl(route) {
	let imSw = getUrlParams().get('im_sw');
	if (imSw === null || imSw === '') {
		const query = (route && route.query) || {};
		imSw = query.im_sw;
	}
	if (imSw === undefined || imSw === null || imSw === '') {
		return null;
	}
	return String(imSw) === '1';
}

function getCachedImSw() {
	const cached = sessionStorage.getItem(IM_SW_SESSION_KEY);
	if (cached === null || cached === '') {
		return null;
	}
	return cached === '1';
}

function setCachedImSw(supported) {
	sessionStorage.setItem(IM_SW_SESSION_KEY, supported ? '1' : '0');
}

/**
 * im_sw 优先读当前 URL，否则读会话缓存
 * APP 仅在入口拼接 im_sw，H5 内部跳转不会携带，需会话级保持
 */
function resolveImSw(route) {
	const fromUrl = getImSwFromUrl(route);
	if (fromUrl !== null) {
		setCachedImSw(fromUrl);
		return fromUrl;
	}
	return getCachedImSw();
}

/** 任意页面 URL 带 im_sw 时写入 session（路由守卫 / 页面入口调用） */
export function cacheImSwFromRoute(route) {
	const fromUrl = getImSwFromUrl(route);
	if (fromUrl === null) {
		return;
	}
	setCachedImSw(fromUrl);
}

/** 应用启动时从入口 URL 捕获 im_sw（早于路由重定向） */
export function cacheImSwFromEntryUrl() {
	const imSw = getUrlParams().get('im_sw');
	if (imSw === null || imSw === '') {
		return;
	}
	setCachedImSw(String(imSw) === '1');
}

/** im_sw=1（URL 或 session） */
export function isImSwEnabled(route) {
	return resolveImSw(route) === true;
}

/** im_sw=0 时不请求 getDeviceInfoByIccid */
export function isImSwDisabled(route) {
	return resolveImSw(route) === false;
}

/** 缓存 iccid 对应的 deviceId，并通知客服入口刷新 */
export function cacheDeviceIdByIccid(iccid, deviceId) {
	if (!iccid || !deviceId) {
		return;
	}
	setCachedDeviceIdByIccid(iccid, deviceId);
	window.dispatchEvent(
		new CustomEvent(DEVICE_ID_UPDATED_EVENT, { detail: { iccid, deviceId } })
	);
}

export const CUSTOMER_SERVICE_DEVICE_ID_UPDATED_EVENT = DEVICE_ID_UPDATED_EVENT;

function getIccidForDeviceResolve(route) {
	const fromUrl = getIccidFromUrl(route);
	if (fromUrl) {
		return fromUrl;
	}
	if (route && route.path === PACKAGE_PURCHASE_ROUTE) {
		return localStorage.getItem('iccid') || '';
	}
	return '';
}

/** 设备 ID：URL deviceId 优先；packagePurchase 通过页面接口缓存换取 */
export function getDeviceId(route) {
	const urlDeviceId = getDeviceIdFromUrl(route);
	if (urlDeviceId) {
		return urlDeviceId;
	}

	const iccid = getIccidForDeviceResolve(route);
	if (iccid) {
		return getCachedDeviceIdByIccid(iccid) || '';
	}

	return '';
}

function getCachedImSupport(deviceId) {
	try {
		const raw = sessionStorage.getItem(IM_SUPPORT_SESSION_KEY);
		if (!raw) return null;
		const { deviceId: cachedId, supported } = JSON.parse(raw);
		if (deviceId && cachedId && deviceId !== cachedId) {
			return null;
		}
		return supported;
	} catch (e) {
		return null;
	}
}

function setCachedImSupport(deviceId, supported) {
	sessionStorage.setItem(
		IM_SUPPORT_SESSION_KEY,
		JSON.stringify({ deviceId: deviceId || '', supported })
	);
}

let pendingIosImSupportResolve = null;
let pendingIosImSupportPromise = null;
let iosImSupportCallbackReady = false;

function logCheckImSupportResult(platform, raw, deviceId) {
	const supported = Number(raw) === 1;
	console.log(`[客服入口排查-CheckImSupport] ${platform}`, {
		调用参数: { deviceId: deviceId || '', entry: IM_ENTRY },
		原生返回值: raw,
		解析后: supported ? '1=支持' : '0=不支持',
		页面应显示客服入口: supported ? '要显示' : '不显示',
	});
	return supported;
}

function hasIosMessageHandler(name) {
	return !!(
		window.webkit &&
		window.webkit.messageHandlers &&
		window.webkit.messageHandlers[name]
	);
}

function hasIosCheckImSupport() {
	return hasIosMessageHandler('CheckImSupport');
}

function hasIosOpenIM() {
	return hasIosMessageHandler('OpenIM');
}

function getImOpenPayloadObject(route) {
	const feedbackQuery = route ? getFeedbackRouteQuery(route) : {};
	return {
		entry_addr: IM_ENTRY,
		...feedbackQuery,
	};
}

/** 从路由解析 Feedback 跳转需携带的 iccid / deviceId */
export function pickFeedbackQuery(route) {
	const query = {};
	const iccid = getQueryValue(route, 'iccid');
	const deviceId = getQueryValue(route, 'deviceId');
	if (iccid) {
		query.iccid = iccid;
	}
	if (deviceId) {
		query.deviceId = deviceId;
	}
	return query;
}

export function getFeedbackRouteQuery(route) {
	const fromQuery = pickFeedbackQuery(route);
	if (fromQuery.iccid || fromQuery.deviceId) {
		return fromQuery;
	}

	const query = {};
	const iccid = getIccidForDeviceResolve(route);
	const deviceId = getDeviceId(route);
	if (iccid) {
		query.iccid = iccid;
	}
	if (deviceId) {
		query.deviceId = deviceId;
	}
	return query;
}

function ensureIosImSupportCallback() {
	if (iosImSupportCallbackReady) {
		return;
	}
	window.callbackIMSupport = function callbackIMSupport(imSupport) {
		const supported = logCheckImSupportResult('iOS 回调返回', imSupport, '');
		if (pendingIosImSupportResolve) {
			const resolve = pendingIosImSupportResolve;
			pendingIosImSupportResolve = null;
			pendingIosImSupportPromise = null;
			resolve(supported);
		}
	};
	iosImSupportCallbackReady = true;
}

ensureIosImSupportCallback();

/**
 * Android 同步查询是否支持 IM 客服入口
 * @returns {boolean|null} 有结果返回 true/false，无接口返回 null
 */
function checkImSupportAndroid(deviceId) {
	if (!window.android || typeof window.android.CheckImSupport !== 'function') {
		return null;
	}
	try {
		logDevTrace('calling CheckImSupport (Android)', {
			deviceId: deviceId || '',
			entry: IM_ENTRY,
		});
		const result = window.android.CheckImSupport(deviceId || '', IM_ENTRY);
		return logCheckImSupportResult('Android 返回', result, deviceId);
	} catch (e) {
		console.warn('[CustomerService] CheckImSupport failed', e);
		return false;
	}
}

/**
 * iOS 异步查询是否支持 IM 客服入口
 * @returns {Promise<boolean|null>} 有结果返回 true/false，无接口返回 null
 */
function checkImSupportIOS(deviceId) {
	if (!hasIosCheckImSupport()) {
		return Promise.resolve(null);
	}

	ensureIosImSupportCallback();

	if (pendingIosImSupportPromise) {
		return pendingIosImSupportPromise;
	}

	const promise = new Promise((resolve) => {
		pendingIosImSupportResolve = resolve;
	});
	pendingIosImSupportPromise = promise;

	try {
		const params = { deviceId: deviceId || '', entry: IM_ENTRY };
		console.log('[客服入口排查-CheckImSupport] iOS 开始调用', params);
		window.webkit.messageHandlers.CheckImSupport.postMessage(params);
	} catch (e) {
		const resolve = pendingIosImSupportResolve;
		pendingIosImSupportResolve = null;
		pendingIosImSupportPromise = null;
		console.warn('[CustomerService] CheckImSupport iOS failed', e);
		resolve(false);
	}
	return promise;
}

function resolveImSupportEarly(route, options = {}) {
	const skipImSwShortcut = options.skipImSwShortcut === true;
	const imSw = resolveImSw(route);

	if (imSw === false) {
		logDevTrace('hidden: im_sw=0');
		return false;
	}

	if (!canShowOnCurrentRoute(route)) {
		logDevTrace('hidden: current page has no iccid/deviceId in URL');
		return false;
	}

	if (imSw === true && !skipImSwShortcut) {
		logDevTrace('shown: im_sw=1, skip CheckImSupport');
		return true;
	}

	return null;
}

function resolveImSupportFromCache(route, deviceId) {
	const earlyResult = resolveImSupportEarly(route);
	if (earlyResult !== null) {
		return earlyResult;
	}

	if (!deviceId) {
		return null;
	}

	const cachedSupport = getCachedImSupport(deviceId);
	if (cachedSupport !== null) {
		return cachedSupport;
	}

	return null;
}

/** 是否展示客服入口（同步，仅 im_sw / 缓存 / Android） */
export function resolveImSupport(route) {
	const devMockResult = resolveDevMockImSupport(route);
	if (devMockResult !== null) {
		return devMockResult;
	}

	const deviceId = getDeviceId(route);
	const cachedResult = resolveImSupportFromCache(route, deviceId);
	if (cachedResult !== null) {
		return cachedResult;
	}

	applyDevMockBridge(route);

	if (!deviceId) {
		return false;
	}

	const androidResult = checkImSupportAndroid(deviceId);
	if (androidResult !== null) {
		setCachedImSupport(deviceId, androidResult);
		return androidResult;
	}

	return false;
}

/** 是否展示客服入口（含 iccid 换 deviceId 与 iOS 异步查询） */
export async function resolveImSupportAsync(route) {
	applyDevMockBridge(route);

	const devMockResult = resolveDevMockImSupport(route);
	if (devMockResult !== null) {
		return devMockResult;
	}

	const forceCheckImSupport = isDevMockBridgeMode(route);
	const earlyResult = resolveImSupportEarly(route, {
		skipImSwShortcut: forceCheckImSupport,
	});
	if (earlyResult !== null) {
		return earlyResult;
	}

	const deviceId = getDeviceId(route);

	logDevTrace('resolved deviceId', {
		deviceId: deviceId || '(empty)',
		iccid: getIccidForDeviceResolve(route) || '(empty)',
		forceCheckImSupport,
	});

	const cachedResult = resolveImSupportFromCache(route, deviceId);
	if (!forceCheckImSupport && cachedResult !== null) {
		logDevTrace('use cached CheckImSupport result', cachedResult);
		return cachedResult;
	}

	if (!deviceId) {
		logDevTrace('hidden: deviceId empty (check getDeviceInfoByIccid API)');
		return false;
	}

	const androidResult = checkImSupportAndroid(deviceId);
	if (androidResult !== null) {
		setCachedImSupport(deviceId, androidResult);
		return androidResult;
	}

	const iosResult = await checkImSupportIOS(deviceId);
	if (iosResult !== null) {
		setCachedImSupport(deviceId, iosResult);
		return iosResult;
	}

	logDevTrace('hidden: native CheckImSupport bridge not available');
	return false;
}

/** 点击客服入口，唤起原生 IM */
export function openCustomerService(route) {
	const payloadObj = getImOpenPayloadObject(route);

	if (window.android && typeof window.android.OpenIM === 'function') {
		window.android.OpenIM(JSON.stringify(payloadObj));
		return;
	}

	if (hasIosOpenIM()) {
		try {
			window.webkit.messageHandlers.OpenIM.postMessage(payloadObj);
			return;
		} catch (e) {
			console.warn('[CustomerService] OpenIM iOS failed', e);
		}
	}

	console.warn('[CustomerService] OpenIM not available');
}

/** 点击反馈入口：有原生 Bridge 时通知 APP（返回 true），H5 侧仍自行跳转反馈页 */
export function openFeedback(route) {
	const payloadObj = getImOpenPayloadObject(route);

	// 开发 mock 仅打日志，不拦截 H5 跳转
	if (isDevMockBridgeMode(route)) {
		logDevTrace('OpenFeedback (mock, H5 will navigate)', payloadObj);
		return false;
	}

	if (window.android && typeof window.android.OpenFeedback === 'function') {
		window.android.OpenFeedback(JSON.stringify(payloadObj));
		return true;
	}

	if (hasIosMessageHandler('OpenFeedback')) {
		try {
			window.webkit.messageHandlers.OpenFeedback.postMessage(payloadObj);
			return true;
		} catch (e) {
			console.warn('[CustomerService] OpenFeedback iOS failed', e);
		}
	}

	return false;
}
